import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { makeStyles } from '@mui/styles';
import {
	Button,
	Card,
	CardContent,
	CardActions,
	Grid,
	Divider,
	Checkbox,
	TextField,
	Autocomplete,
	Typography,
	Paper,
	Button as MuiButton,
	colors
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DatePicker from 'components/DatePicker/DatePicker';
import { SelectField } from 'components';
import { enqueueSnackbar } from 'actions';
import validate from 'validate.js';

import { ExportChartsToPdf } from './exportChartPdf';
import { getChartEntries } from './chartRegistry';

import {
	SetFormState,
	GetSites,
	GetCells,
	GetKpiSeries,
	ExportKpiSeries,
	ResetSeries
} from '../../actions/KPIsOverviewActions';

const useStyles = makeStyles((theme) => ({
	content: {
		marginTop: theme.spacing(3)
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900]
		}
	},
	export: {
		marginLeft: theme.spacing(1)
	},
	selectActions: {
		display: 'flex',
		justifyContent: 'space-between',
		padding: theme.spacing(0.5, 1),
		borderBottom: `1px solid ${theme.palette.divider}`
	}
}));

const schema = {
	granularity: {
		presence: { allowEmpty: false, message: 'is required' }
	},
	technology: {
		presence: { allowEmpty: false, message: 'is required' }
	},
	startDate: {
		presence: { allowEmpty: false, message: 'is required' }
	},
	endDate: {
		presence: { allowEmpty: false, message: 'is required' }
	}
};

/* Janela maxima de consulta, para nao rebentar com a query nem com o grafico. */
const MAX_DAYS_DAILY = 92;
const MAX_DAYS_HOURLY = 14;

/*
 * Numero maximo de KPIs por consulta.
 *
 * E desenhado um grafico (um <canvas>) por KPI, e uma coluna de tabela por KPI.
 * O 4G tem 48 KPIs marcados como "Main"; desenhar 48 canvas de uma vez esgota a
 * memoria do separador do browser e o Chrome mata a pagina. O Tableau mostrava
 * todos, mas em paineis minusculos e com um motor de desenho proprio.
 */
const MAX_KPIS = 8;

/*
 * Modos de visualizacao:
 *   'separate' -> um grafico por KPI (comportamento original);
 *   'dual'     -> UM grafico com dois eixos Y, comparando dois KPIs.
 */
const CHART_MODES = [
	{ value: 'separate', text: 'Separate charts' },
	{ value: 'dual', text: 'Dual axis (compare 2 KPIs)' }
];

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const KPIsOverviewForm = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));

	const formState = useSelector(state => state.kpisOverviewReducer.formState);
	const metadata = useSelector(state => state.kpisOverviewReducer.metadata);
	const sites = useSelector(state => state.kpisOverviewReducer.sites);
	const cells = useSelector(state => state.kpisOverviewReducer.cells);
	const cellsKey = useSelector(state => state.kpisOverviewReducer.cellsKey);
	const sitesKey = useSelector(state => state.kpisOverviewReducer.sitesKey);
	const isLoading = useSelector(state => state.kpisOverviewReducer.isLoading);
	const hasResult = useSelector(state => state.kpisOverviewReducer.hasResult);
	const user = useSelector(state => state.session.user);

	const values = formState.values;
	const technology = metadata.catalogue[values.technology];

	/* Modo de grafico (separate | dual). */
	const isDual = values.chartMode === 'dual';

	/* Niveis de agregacao e KPIs disponiveis, lidos do catalogo. */
	const isHourly = values.granularity === 'hourly';
	let aggregationLevelOptions = [];
	let kpiOptions = [];

	if (technology) {
		aggregationLevelOptions = technology.aggregationLevels.filter(level => (
			isHourly ? level.availableHourly : level.availableDaily
		));

		kpiOptions = technology.kpis.filter((kpi) => (
			isHourly ? kpi.availableHourly : kpi.availableDaily
		));
	}

	/*
	 * A lista de celulas so faz sentido para a combinacao que a gerou. Se o
	 * utilizador mudou tecnologia/granularidade/sites desde o ultimo Apply, a
	 * key deixa de bater certo e mostramos o dropdown vazio (a pedir novo
	 * Apply) em vez de celulas de outros sites.
	 */
	const currentCellsKey = `${values.technology}|${values.granularity}|${[...values.siteIds].sort().join(',')}`;
	const cellsMatchFilters = cellsKey === currentCellsKey;
	const cellOptions = cellsMatchFilters ? cells : [];

	/* Validacao. */
	useEffect(() => {
		const errors = validate(values, schema) || {};

		if (!values.aggregationLevels.length) {
			errors.aggregationLevels = ['Select at least one aggregation level'];
		}

		if (!values.siteIds.length) {
			errors.siteIds = ['Select at least one site'];
		}

		/*
		 * Em modo 'dual' os KPIs escolhem-se em dois selects individuais (Y1/Y2);
		 * em modo 'separate' usa-se o multiselect com o limite de MAX_KPIS.
		 */
		if (isDual) {
			if (!values.y1KpiId) {
				errors.y1KpiId = ['Select the primary KPI (Y1)'];
			}
			if (!values.y2KpiId) {
				errors.y2KpiId = ['Select the secondary KPI (Y2)'];
			} else if (values.y1KpiId && values.y2KpiId === values.y1KpiId) {
				errors.y2KpiId = ['Y2 must be different from Y1'];
			}
		} else if (!values.kpiIds.length) {
			errors.kpiIds = ['Select at least one KPI'];
		} else if (values.kpiIds.length > MAX_KPIS) {
			errors.kpiIds = [`Select at most ${MAX_KPIS} KPIs (one chart is drawn per KPI)`];
		}

		if (values.startDate && values.endDate) {
			const days = moment(values.endDate).diff(moment(values.startDate), 'days');
			const maxDays = isHourly ? MAX_DAYS_HOURLY : MAX_DAYS_DAILY;

			if (days < 0) {
				errors.endDate = ['End date must be after start date'];
			} else if (days > maxDays) {
				errors.endDate = [`Maximum range for ${values.granularity} data is ${maxDays} days`];
			}
		}

		setFormState({
			...formState,
			isValid: Object.keys(errors).length === 0,
			errors
		});
	}, [values]);

	/*
	 * A lista de sites e a data do ultimo update dependem da tecnologia e da
	 * granularidade. So voltamos a pedi-las quando essa combinacao muda -- se
	 * pedissemos a cada montagem, cada resposta criava um array novo no estado
	 * e provocava novo render, sem nunca estabilizar.
	 */
	useEffect(() => {
		if (sitesKey !== `${values.technology}|${values.granularity}`) {
			dispatch(GetSites(values.technology, values.granularity));
		}
	}, [values.technology, values.granularity]);

	/*
	 * Ao mudar de tecnologia, os KPIs e os sites escolhidos deixam de fazer
	 * sentido (o 4G nao tem os mesmos KPIs do 5G), por isso limpamos.
	 */
	const _handleChangeTechnology = (event) => {
		setFormState({
			...formState,
			values: {
				...values,
				technology: event.target.value,
				aggregationLevels: ['site'],
				siteIds: [],
				cellIds: [],
				kpiIds: [],
				y1KpiId: null,
				y2KpiId: null
			},
			touched: {
				...formState.touched,
				technology: true
			}
		});
		dispatch(ResetSeries());
	};

	const _handleChangeGranularity = (event) => {
		setFormState({
			...formState,
			values: {
				...values,
				granularity: event.target.value,
				cellIds: [],
				kpiIds: [],
				y1KpiId: null,
				y2KpiId: null
			},
			touched: {
				...formState.touched,
				granularity: true
			}
		});
		dispatch(ResetSeries());
	};

	/*
	 * Alternar entre gráficos separados e duplo eixo. So muda o modo de
	 * visualizacao; os KPIs ja escolhidos mantem-se para o utilizador poder
	 * voltar atras sem perder a seleccao.
	 */
	const _handleChangeChartMode = (event) => {
		setFormState({
			...formState,
			values: {
				...values,
				chartMode: event.target.value
			},
			touched: {
				...formState.touched,
				chartMode: true
			}
		});
	};

	const _handleChangeY1 = (event) => {
		setFormState({
			...formState,
			values: {
				...values,
				y1KpiId: event.target.value
			},
			touched: {
				...formState.touched,
				y1KpiId: true
			}
		});
	};

	const _handleChangeY2 = (event) => {
		setFormState({
			...formState,
			values: {
				...values,
				y2KpiId: event.target.value
			},
			touched: {
				...formState.touched,
				y2KpiId: true
			}
		});
	};

	const _handleAggregationLevelsChange = (selected) => {
		setFormState({
			...formState,
			values: {
				...values,
				aggregationLevels: selected.map(option => option.value)
			},
			touched: {
				...formState.touched,
				aggregationLevels: true
			}
		});
	};

	const _handleDateChange = (value, name) => {
		setFormState({
			...formState,
			values: {
				...values,
				[name]: value
			},
			touched: {
				...formState.touched,
				[name]: true
			}
		});
	};

	/*
	 * O Work Day define a janela: ao muda-lo, o Start Date passa a -7 dias e o
	 * End Date a +7 dias (uma janela de 14 dias centrada no dia escolhido). O
	 * utilizador pode depois ajustar Start/End a mao. moment e mutavel, por
	 * isso cada moment(value) cria uma copia nova antes de somar/subtrair.
	 */
	const _handleWorkDayChange = (value) => {
		setFormState({
			...formState,
			values: {
				...values,
				workDay: value,
				startDate: value ? moment(value).subtract(7, 'days') : values.startDate,
				endDate: value ? moment(value).add(7, 'days') : values.endDate
			},
			touched: {
				...formState.touched,
				workDay: true,
				startDate: true,
				endDate: true
			}
		});
	};

	/*
	 * Mudar de sites invalida a lista de celulas ja carregada -- as celulas
	 * pertenciam aos sites anteriores. Limpamos a seleccao de celulas; a lista
	 * volta a ser pedida no proximo Apply.
	 */
	const _handleSitesChange = (selected) => {
		setFormState({
			...formState,
			values: {
				...values,
				siteIds: selected.map(option => option.value),
				cellIds: []
			},
			touched: {
				...formState.touched,
				siteIds: true
			}
		});
	};

	const _handleCellsChange = (selected) => {
		setFormState({
			...formState,
			values: {
				...values,
				cellIds: selected.map(option => option.value)
			},
			touched: {
				...formState.touched,
				cellIds: true
			}
		});
	};

	const _handleKpisChange = (selected) => {
		setFormState({
			...formState,
			values: {
				...values,
				kpiIds: selected.map(option => option.value)
			},
			touched: {
				...formState.touched,
				kpiIds: true
			}
		});
	};

	/*
	 * "Select all" dos KPIs. Como so se desenham MAX_KPIS graficos (um <canvas>
	 * por KPI), seleccionar todos seleccionaria demasiados e o Apply ficaria
	 * bloqueado pela validacao. Por isso seleccionamos os primeiros MAX_KPIS e,
	 * quando ha mais do que isso, avisamos o utilizador.
	 */
	const _handleSelectAllKpis = () => {
		const capped = kpiOptions.slice(0, MAX_KPIS);
		_handleKpisChange(capped);

		if (kpiOptions.length > MAX_KPIS) {
			dispatch(enqueueSnackbar({
				message: `Selected the first ${MAX_KPIS} of ${kpiOptions.length} KPIs (maximum per query).`,
				options: { variant: 'info' }
			}));
		}
	};

	/*
	 * O Work Day nao vai para a API -- serve para desenhar a linha vermelha nos
	 * graficos e para definir a janela (+/-7 dias). Nao entra no `schema` nem
	 * bloqueia o Apply; se o utilizador ajustar Start/End e o Work Day cair fora
	 * do intervalo, avisamos e mais nada.
	 */
	const workDayOutOfRange = !!(values.workDay && values.startDate && values.endDate
		&& !moment(values.workDay).isBetween(
			moment(values.startDate), moment(values.endDate), 'day', '[]'
		));

	const _hasError = (field) => (!!formState.errors[field]);

	const _helperText = (field) => (formState.errors[field] ? formState.errors[field][0] : null);

	/*
	 * KPIs efectivamente enviados a API. Em modo 'dual' sao os dois eixos
	 * (Y1 e Y2); em modo 'separate' e a lista do multiselect.
	 */
	const _effectiveKpiIds = () => (
		isDual ? [values.y1KpiId, values.y2KpiId].filter(Boolean) : values.kpiIds
	);

	/* Objecto enviado a API. As datas vao em texto, no formato ISO. */
	const _getValues = () => ({
		granularity: values.granularity,
		technology: values.technology,
		aggregationLevels: values.aggregationLevels,
		siteIds: values.siteIds,
		/* Vazio = todas as celulas dos sites escolhidos. */
		cellIds: values.cellIds,
		kpiIds: _effectiveKpiIds(),
		startDate: moment(values.startDate).format('YYYY-MM-DD'),
		endDate: moment(values.endDate).format('YYYY-MM-DD')
	});

	/*
	 * Apply: desenha os graficos e, se a combinacao tecnologia|granularidade|
	 * sites tiver mudado, vai buscar a lista de celulas desses sites (para o
	 * utilizador poder depois filtra-las e fazer Apply outra vez). Se a
	 * combinacao nao mudou, nao repetimos GetCells -- so o GetKpiSeries.
	 */
	const _apply = () => {
		if (!cellsMatchFilters) {
			dispatch(GetCells(values.technology, values.granularity, values.siteIds));
		}
		dispatch(GetKpiSeries(_getValues(), user.id));
	};

	/* Refresh: volta a desenhar com os mesmos filtros (ex.: dados mais frescos). */
	const _refresh = () => {
		dispatch(GetKpiSeries(_getValues(), user.id));
	};

	const _export = () => {
		dispatch(ExportKpiSeries(_getValues(), user.id));
	};

	/*
	 * Exporta para PDF todos os graficos actualmente no ecra (um por pagina). As
	 * instancias vem do registo partilhado, onde cada grafico se inscreve.
	 */
	const _exportAllPdf = () => {
		ExportChartsToPdf(getChartEntries(), 'kpis_overview.pdf');
	};

	const selectedAggregationLevels = aggregationLevelOptions.filter(
		level => values.aggregationLevels.includes(level.value)
	);
	const selectedSites = sites.filter(site => values.siteIds.includes(site.value));
	const selectedCells = cellOptions.filter(cell => values.cellIds.includes(cell.value));
	const selectedKpis = kpiOptions.filter(kpi => values.kpiIds.includes(kpi.value));

	/*
	 * O Autocomplete do MUI nao tem "select all" nativo. O padrao e injectar
	 * uma barra de accoes no topo do dropdown via PaperComponent. O
	 * onMouseDown/preventDefault impede o dropdown de fechar antes do onClick.
	 */
	const _makeSelectAllPaper = (onSelectAll, onClear) => (paperProps) => (
		<Paper {...paperProps}>
			<div
				className={classes.selectActions}
				onMouseDown={(event) => event.preventDefault()}
			>
				<MuiButton size="small" onClick={onSelectAll}>Select all</MuiButton>
				<MuiButton size="small" color="inherit" onClick={onClear}>Clear all</MuiButton>
			</div>
			{paperProps.children}
		</Paper>
	);

	/* Texto de ajuda do dropdown de celulas, consoante o estado. */
	const _cellsHelperText = () => {
		if (!values.siteIds.length) return 'Select site(s) first';
		if (!cellsMatchFilters) return 'Click Apply to load the cells of the selected site(s)';
		return `${values.cellIds.length} selected, ${cellOptions.length} available (empty = all cells)`;
	};

	return (
		<Card className={classes.content}>
			<CardContent>
				<Grid container spacing={2}>
					<Grid item md={2} xs={6}>
						<SelectField
							id="chartMode"
							name="chartMode"
							value={values.chartMode || 'separate'}
							onChange={_handleChangeChartMode}
							hintText="Chart mode"
							menuItems={CHART_MODES}
						/>
					</Grid>
					<Grid item md={2} xs={6}>
						<SelectField
							id="granularity"
							name="granularity"
							value={values.granularity || ''}
							onChange={_handleChangeGranularity}
							hintText="Granularity"
							menuItems={metadata.granularities}
							helperText={_helperText('granularity')}
						/>
					</Grid>
					<Grid item md={2} xs={6}>
						<SelectField
							id="technology"
							name="technology"
							value={values.technology || ''}
							onChange={_handleChangeTechnology}
							hintText="Technology"
							menuItems={metadata.technologies}
							helperText={_helperText('technology')}
						/>
					</Grid>
					<Grid item md={2} xs={6}>
						<DatePicker
							label="Work Day"
							name="workDay"
							value={values.workDay || null}
							onChange={(value) => _handleWorkDayChange(value)}
							helperText={workDayOutOfRange ? 'Outside the selected range' : 'Sets Start/End to +/-7 days'}
						/>
					</Grid>
					<Grid item md={2} xs={6}>
						<DatePicker
							label="Start Date"
							name="startDate"
							value={values.startDate || null}
							onChange={(value) => _handleDateChange(value, 'startDate')}
							error={_hasError('startDate')}
							helperText={_helperText('startDate')}
						/>
					</Grid>
					<Grid item md={2} xs={6}>
						<DatePicker
							label="End Date"
							name="endDate"
							value={values.endDate || null}
							onChange={(value) => _handleDateChange(value, 'endDate')}
							error={_hasError('endDate')}
							helperText={_helperText('endDate')}
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							limitTags={4}
							options={aggregationLevelOptions}
							value={selectedAggregationLevels}
							getOptionLabel={(option) => option.text}
							isOptionEqualToValue={(option, value) => option.value === value.value}
							onChange={(event, selected) => _handleAggregationLevelsChange(selected)}
							renderOption={(props, option, { selected }) => (
								<li {...props}>
									<Checkbox
										icon={icon}
										checkedIcon={checkedIcon}
										style={{ marginRight: 8 }}
										checked={selected}
									/>
									{option.text}
								</li>
							)}
							renderInput={(params) => (
								<TextField
									{...params}
									fullWidth
									variant="outlined"
									label="Aggregation level(s)"
									error={_hasError('aggregationLevels')}
									helperText={_helperText('aggregationLevels')
										|| 'One line per combination, e.g. Site + Sector = "10025, 2"'}
								/>
							)}
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							limitTags={4}
							options={sites}
							value={selectedSites}
							PaperComponent={_makeSelectAllPaper(
								() => _handleSitesChange(sites),
								() => _handleSitesChange([])
							)}
							getOptionLabel={(option) => option.text}
							isOptionEqualToValue={(option, value) => option.value === value.value}
							onChange={(event, selected) => _handleSitesChange(selected)}
							renderOption={(props, option, { selected }) => (
								<li {...props}>
									<Checkbox
										icon={icon}
										checkedIcon={checkedIcon}
										style={{ marginRight: 8 }}
										checked={selected}
									/>
									{option.text}
								</li>
							)}
							renderInput={(params) => (
								<TextField
									{...params}
									fullWidth
									variant="outlined"
									label="Site(s)"
									error={_hasError('siteIds')}
									helperText={_helperText('siteIds')}
								/>
							)}
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							limitTags={4}
							options={cellOptions}
							value={selectedCells}
							disabled={!cellOptions.length}
							PaperComponent={_makeSelectAllPaper(
								() => _handleCellsChange(cellOptions),
								() => _handleCellsChange([])
							)}
							getOptionLabel={(option) => option.text}
							isOptionEqualToValue={(option, value) => option.value === value.value}
							onChange={(event, selected) => _handleCellsChange(selected)}
							renderOption={(props, option, { selected }) => (
								<li {...props}>
									<Checkbox
										icon={icon}
										checkedIcon={checkedIcon}
										style={{ marginRight: 8 }}
										checked={selected}
									/>
									{option.text}
								</li>
							)}
							renderInput={(params) => (
								<TextField
									{...params}
									fullWidth
									variant="outlined"
									label="Cell(s) - optional"
									helperText={_cellsHelperText()}
								/>
							)}
						/>
					</Grid>

					{/*
					 * KPIs. Em modo 'dual' escolhem-se dois KPIs (Y1/Y2) em selects
					 * individuais; em modo 'separate' usa-se o multiselect com
					 * "Select all" (limitado a MAX_KPIS).
					 */}
					{isDual ? (
						<>
							<Grid item md={6} xs={12}>
								<SelectField
									id="y1KpiId"
									name="y1KpiId"
									value={values.y1KpiId || ''}
									onChange={_handleChangeY1}
									hintText="Primary KPI (Y1 - left axis)"
									menuItems={kpiOptions}
									helperText={_helperText('y1KpiId')}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<SelectField
									id="y2KpiId"
									name="y2KpiId"
									value={values.y2KpiId || ''}
									onChange={_handleChangeY2}
									hintText="Secondary KPI (Y2 - right axis)"
									menuItems={kpiOptions}
									helperText={_helperText('y2KpiId')}
								/>
							</Grid>
						</>
					) : (
						<Grid item md={12} xs={12}>
							<Autocomplete
								multiple
								disableCloseOnSelect
								limitTags={4}
								options={kpiOptions}
								value={selectedKpis}
								PaperComponent={_makeSelectAllPaper(
									_handleSelectAllKpis,
									() => _handleKpisChange([])
								)}
								getOptionLabel={(option) => option.text}
								isOptionEqualToValue={(option, value) => option.value === value.value}
								onChange={(event, selected) => _handleKpisChange(selected)}
								renderOption={(props, option, { selected }) => (
									<li {...props}>
										<Checkbox
											icon={icon}
											checkedIcon={checkedIcon}
											style={{ marginRight: 8 }}
											checked={selected}
										/>
										{option.text}
									</li>
								)}
								renderInput={(params) => (
									<TextField
										{...params}
										fullWidth
										variant="outlined"
										label={`KPI(s) - ${values.kpiIds.length}/${MAX_KPIS} selected, ${kpiOptions.length} available`}
										error={_hasError('kpiIds')}
										helperText={_helperText('kpiIds')}
									/>
								)}
							/>
						</Grid>
					)}
				</Grid>
			</CardContent>
			<Divider />
			<CardActions>
				<Button
					className={classes.generate}
					type="button"
					onClick={_apply}
					variant="contained"
					disabled={!formState.isValid || isLoading}
				>
					{isLoading ? 'Loading...' : 'Apply'}
				</Button>
				<Button
					className={classes.export}
					type="button"
					onClick={_refresh}
					variant="outlined"
					disabled={!formState.isValid || isLoading || !hasResult}
				>
					Refresh
				</Button>
				<Button
					className={classes.export}
					type="button"
					onClick={_export}
					variant="outlined"
					disabled={!formState.isValid || isLoading}
				>
					Export to Excel
				</Button>
				<Button
					className={classes.export}
					type="button"
					onClick={_exportAllPdf}
					variant="outlined"
					disabled={isLoading || !hasResult}
				>
					Export all to PDF
				</Button>
			</CardActions>
		</Card>
	);
};

export default KPIsOverviewForm;